/**
 * Yahoo accounts, and which manager each one is.
 *
 * This screen exists because of a gap in Yahoo's permissions. The design was that nobody links
 * anything by hand: a manager signs in, the server asks Yahoo what he owns, Yahoo names his team
 * in league 338028, done. That still happens — but only once Yahoo approves the Fantasy Sports
 * API, and approval is a queue.
 *
 * Until it clears, sign-in works and the team lookup does not, so every account arrives
 * unrecognised. Ten one-time decisions. That is a perfectly good state to be in and a terrible
 * state to have no screen for, which is what this is.
 *
 * The important property: nothing changes when approval lands. The server keeps writing links the
 * same way; it simply starts filling in the team and the suggested manager, and the rows below go
 * from "no team reported" to "Yahoo says he owns X". No redeploy, no migration, no second design.
 */

import { useCallback, useEffect, useState } from 'react';
import { allManagers, currentManagers, managerShort } from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import { GateTag } from './Gate.js';
import { atLeast, type Role } from '../store/types.js';
import { idToken } from '../store/session.js';

interface Link {
  uid: string;
  gm: string | null;
  confirmed: boolean;
  teamName: string | null;
  teamKey: string | null;
  why: string;
  confirmedBy?: string;
  /** Signed in to watch. Recognised, holds no team, writes nothing. */
  viewer?: boolean;
}

export function AccountLinks({ role }: { role: Role }) {
  /*
   * THE TOKEN IS FETCHED HERE, and it used to be a prop nobody passed.
   *
   * `TeamPairing` rendered `<AccountLinks role={role} />` and the component's every request began
   * `if (!idToken) return;` — so the fetch never fired, the list never loaded, and the screen sat
   * there showing "Only works on the deployed site" ON the deployed site. It looked like a
   * feature waiting on Yahoo's approval rather than a wire that was never connected, which is why
   * it survived: a screen that is supposed to be empty until people sign in, and is empty.
   *
   * A prop cannot go unpassed if there is no prop. `idToken()` is the same helper Ask the Record
   * uses; it resolves to null when there is no signed-in user, which is the honest offline case.
   */
  const [token, setToken] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void idToken().then((t) => { if (live) setToken(t); });
    return () => { live = false; };
  }, []);

  const [links, setLinks] = useState<Link[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const gms = currentManagers().map((m) => m.id);
  /*
   * The people who may be paired to an account but hold no seat.
   *
   * They are a SEPARATE optgroup rather than more names in the same list, because the two choices
   * mean different things: picking a manager hands somebody a team, picking a viewer hands them a
   * chair. A flat list of eleven names with one of them silently different is how the wrong one
   * gets clicked.
   */
  const watchers = allManagers().filter((m) => m.role === 'viewer').map((m) => m.id);

  const load = useCallback(async () => {
    if (!token) return;
    setErr(null);
    try {
      const res = await fetch('/api/links', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json() as { error?: string }).error ?? `HTTP ${res.status}`);
      setLinks((await res.json() as { links: Link[] }).links);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not read the accounts.');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  async function set(uid: string, gm: string | null) {
    if (!token) return;
    setBusy(uid);
    try {
      const res = await fetch('/api/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          uid, gm, confirmed: gm !== null,
          // The server records this; `firestore.rules` reads it inside `linked()`, so a viewer
          // fails every write rule in the file rather than each one having to remember him.
          viewer: gm !== null && watchers.includes(gm),
        }),
      });
      if (!res.ok) throw new Error((await res.json() as { error?: string }).error ?? `HTTP ${res.status}`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setBusy(null);
    }
  }

  if (!atLeast(role, 'admin')) return null;

  return (
    <section className="card gated admin">
      <GateTag level="admin" what="Admin — who is who" />
      <div className="striptop">
        <h3 className="striphead">Yahoo accounts</h3>
        <span className="mut">
          {links ? `${links.filter((l) => l.confirmed).length}/${links.length} confirmed` : ''}
        </span>
      </div>

      <p className="note">
        A manager signs in and the server asks Yahoo what he owns. Once the Fantasy Sports
        permission is approved, Yahoo answers with his team in league 338028 and the right manager
        is proposed here for one click. Until then it can only say who signed in, so the pairing is
        yours to make — ten decisions, once.
      </p>

      {token === undefined && <p className="note">Reading the accounts…</p>}
      {token === null && (
        <p className="note">
          <b>Only works signed in on the deployed site.</b> This reads live accounts from the
          server, and the offline build has no server. Nothing to see here until{' '}
          <code>ffl.timbrown.xyz</code> is up.
        </p>
      )}

      {err && <p className="chip critical block">{err}</p>}

      {token && links?.length === 0 && (
        <p className="note">Nobody has signed in yet. This fills up as they do.</p>
      )}

      {links && links.length > 0 && (
        <table className="records">
          <thead>
            <tr>
              <th>Account</th><th>What Yahoo said</th><th>Manager</th><th />
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.uid} className={l.confirmed ? '' : 'dim'}>
                <td className="mono small">{l.uid.slice(0, 10)}…</td>
                <td>
                  {l.teamName
                    ? <><b>{l.teamName}</b> <span className="mut small">{l.why}</span></>
                    : <span className="mut small">{l.why || 'No team reported.'}</span>}
                </td>
                <td>
                  {l.confirmed && l.gm
                    ? (
                      <span className="linked">
                        <GmBadge gm={l.gm} withName />
                        {l.viewer && <span className="chip tiny">viewer</span>}
                        {l.confirmedBy && (
                          <span className="mut small"> · by {managerShort(l.confirmedBy)}</span>
                        )}
                      </span>
                    )
                    : <span className="chip warning tiny">not linked</span>}
                </td>
                <td className="num">
                  <select
                    value={l.confirmed && l.gm ? l.gm : ''}
                    disabled={busy === l.uid}
                    aria-label={`Manager for account ${l.uid}`}
                    onChange={(e) => void set(l.uid, e.target.value || null)}
                  >
                    <option value="">— nobody —</option>
                    {gms.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
                    {watchers.length > 0 && (
                      <optgroup label="Watching — no team, no actions">
                        {watchers.map((g) => (
                          <option key={g} value={g}>{managerShort(g)}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="note">
        Setting somebody to <b>nobody</b> unlinks the account immediately — that is how a wrong
        link gets undone, and how somebody who should not be in the league is shut out.
        {watchers.length > 0 && (
          <> A name under <b>Watching</b> gets the whole app to read and nothing to press: no team,
            no controls, and no way to act for anybody else. Enforced in the database, not just on
            the screen.</>
        )}
      </p>
    </section>
  );
}
